import { useEffect, useRef, useState } from 'react';

interface FileUploadProps {
  label: string;
  accept: string;
  kind?: 'image' | 'file';
  file: File | null;
  existingUrl?: string | null;
  existingLabel?: string | null;
  onFileChange: (file: File | null) => void;
  onRemoveExisting?: () => void;
  error?: string | null;
  hint?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUpload({
  label,
  accept,
  kind = 'file',
  file,
  existingUrl,
  existingLabel,
  onFileChange,
  onRemoveExisting,
  error,
  hint,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file && kind === 'image') {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return undefined;
  }, [file, kind]);

  const hasImage = kind === 'image' && (previewUrl || existingUrl);
  const displayImage = previewUrl || existingUrl || null;

  const handlePick = () => inputRef.current?.click();

  const handleChange = (selected: File | null) => {
    onFileChange(selected);
  };

  return (
    <div className="w-full">
      <span className="mb-1.5 block text-xs font-semibold text-foreground-700 font-label">
        {label}
      </span>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(event) => {
          const selected = event.target.files?.[0] ?? null;
          handleChange(selected);
          event.target.value = '';
        }}
      />

      {hasImage ? (
        <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 p-3">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
            {displayImage && (
              <img src={displayImage} alt="Vista previa" className="h-full w-full object-cover" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground-900">
              {file ? file.name : existingLabel || 'Imagen cargada'}
            </p>
            <p className="text-xs text-foreground-500">
              {file ? formatSize(file.size) : 'Imagen actual del proyecto'}
            </p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={handlePick}
              aria-label="Cambiar imagen"
              title="Cambiar imagen"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <i className="ri-refresh-line text-base"></i>
            </button>
            <button
              type="button"
              onClick={() => {
                onFileChange(null);
                if (onRemoveExisting) onRemoveExisting();
              }}
              aria-label="Eliminar imagen"
              title="Eliminar imagen"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
            >
              <i className="ri-delete-bin-6-line text-base"></i>
            </button>
          </div>
        </div>
      ) : file ? (
        <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 p-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
            <i className="ri-file-pdf-2-line text-2xl"></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground-900">{file.name}</p>
            <p className="text-xs text-foreground-500">{formatSize(file.size)}</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={handlePick}
              aria-label="Cambiar archivo"
              title="Cambiar archivo"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <i className="ri-refresh-line text-base"></i>
            </button>
            <button
              type="button"
              onClick={() => onFileChange(null)}
              aria-label="Eliminar archivo"
              title="Eliminar archivo"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
            >
              <i className="ri-delete-bin-6-line text-base"></i>
            </button>
          </div>
        </div>
      ) : existingUrl ? (
        <div className="flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 p-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-600">
            <i className="ri-file-pdf-2-line text-2xl"></i>
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground-900">
              {existingLabel || 'Documento de contrato cargado'}
            </p>
            <p className="text-xs text-foreground-500">Documento actual del proyecto</p>
          </div>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={handlePick}
              aria-label="Cambiar documento"
              title="Cambiar documento"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <i className="ri-refresh-line text-base"></i>
            </button>
            {onRemoveExisting && (
              <button
                type="button"
                onClick={onRemoveExisting}
                aria-label="Eliminar documento"
                title="Eliminar documento"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
              >
                <i className="ri-delete-bin-6-line text-base"></i>
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePick}
          className={[
            'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center transition-colors',
            error
              ? 'border-red-300 bg-red-50/40 hover:bg-red-50'
              : 'border-background-300 bg-background-100/50 hover:border-primary-300 hover:bg-primary-50/40',
          ].join(' ')}
        >
          <span
            className={[
              'flex h-11 w-11 items-center justify-center rounded-full',
              error ? 'bg-red-100 text-red-600' : 'bg-background-100 text-foreground-500',
            ].join(' ')}
          >
            <i className={`${kind === 'image' ? 'ri-image-add-line' : 'ri-file-upload-line'} text-xl`}></i>
          </span>
          <span className="text-sm font-medium text-foreground-700">
            {kind === 'image' ? 'Seleccionar imagen' : 'Seleccionar archivo PDF'}
          </span>
          {hint && <span className="text-xs text-foreground-500">{hint}</span>}
        </button>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
          <i className="ri-error-warning-line text-sm"></i>
          {error}
        </p>
      )}
      {!error && hint && (file || existingUrl || hasImage) && (
        <p className="mt-1.5 text-xs text-foreground-500">{hint}</p>
      )}
    </div>
  );
}